import { useState } from 'react';
import {
  IonButton, IonIcon, IonModal, IonHeader, IonToolbar,
  IonItem, IonInput, IonList, IonLabel, IonAlert,
  IonText, IonCardContent
} from '@ionic/react';
import { add, trashOutline, createOutline } from 'ionicons/icons';
import styles from './CustomFieldsManager.module.scss';
import { CustomField } from '@rsmaids/shared/types/contacts'; // Adjust the import path as necessary
import { ApiDataService } from '@rsmaids/shared/services/api/ApiDataService';
import { useTranslation } from 'react-i18next';
import { useSheetScrollGuard } from '../hooks/useSheetScrollGuard';

interface Props {
  existingFields: CustomField[];
  onAddField: (field: CustomField) => void;
  onSelectField?: (fieldId: string) => void;
  onFieldsChanged?: () => Promise<void>;
  // When provided, this modal's visibility is driven externally (e.g. selecting "Custom
  // Field" from a consolidated "Add New Field" menu) instead of this component's own
  // trigger button — pass hideTrigger to suppress that button entirely in that case.
  isOpen?: boolean;
  onDidDismiss?: () => void;
  hideTrigger?: boolean;
}

export const CustomFieldsManager: React.FC<Props> = ({
  existingFields,
  onAddField,
  onSelectField,
  onFieldsChanged,
  isOpen,
  onDidDismiss,
  hideTrigger
}) => {
  const { t } = useTranslation();
  const sheetBodyRef = useSheetScrollGuard();
  const dataService = new ApiDataService();

  const isControlled = isOpen !== undefined;
  const [internalShowModal, setInternalShowModal] = useState(false);
  const showModal = isControlled ? isOpen : internalShowModal;
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');

  const [localFields, setLocalFields] = useState<CustomField[]>([]);

  const [fields, setFields] = useState<CustomField[]>(existingFields);

  // Sync from existingFields whenever it changes, and re-prep the modal's
  // working copy whenever it opens — computed during render (React's
  // "adjust state when a prop changes" pattern) rather than as setState
  // calls inside effects. Runs the "prep on open" logic regardless of
  // whether the modal was opened via this component's own button or
  // externally (isOpen prop flipping to true).
  const [prevExistingFields, setPrevExistingFields] = useState(existingFields);
  if (prevExistingFields !== existingFields) {
    setPrevExistingFields(existingFields);
    console.log(`🔄 CustomFieldsManager: existingFields updated, setting ${existingFields.length} fields`);
    setFields(existingFields);
  }

  const [wasModalOpenForFields, setWasModalOpenForFields] = useState(showModal);
  if (showModal !== wasModalOpenForFields) {
    setWasModalOpenForFields(showModal);
    if (showModal) {
      setFields([...existingFields]);
      setLocalFields([
        ...existingFields,
        {
          id: 'temp-' + crypto.randomUUID(),
          label: '',
          value: '',
          type: 'personal',
          fieldKey: `custom.blank-${crypto.randomUUID()}`
        }
      ]);
    }
  }

  const handleOpenModal = () => {
    if (!isControlled) setInternalShowModal(true);
  };

  const handleCloseModal = () => {
    if (isControlled) {
      onDidDismiss?.();
    } else {
      setInternalShowModal(false);
    }
    setNewFieldLabel('');
    setLocalFields([]);
  };

  const handleAddNew = async () => {
    console.log(`🚀 handleAddNew called with label: "${newFieldLabel}"`);
    
    if (newFieldLabel.trim()) {
      console.log(`✅ Label is not empty: "${newFieldLabel.trim()}"`);
      
      const isDuplicate = existingFields.some(f =>
        f.label.toLowerCase() === newFieldLabel.trim().toLowerCase()
      );
      
      console.log(`🔍 Checking for duplicates. Is duplicate? ${isDuplicate}`);
      console.log(`🔍 Existing fields:`, existingFields.map(f => f.label));
      
      if (!isDuplicate) {
        const fieldKey = `custom.${newFieldLabel.trim().toLowerCase().replace(/\s+/g, '-')}`;
        console.log(`🔧 Generated fieldKey: ${fieldKey}`);

        try {
          console.log(`🔧 Creating new extended field in Wix: ${fieldKey}`);
          
          await dataService.contacts.createExtendedField(fieldKey, newFieldLabel.trim());
          console.log(`✅ Wix field created successfully`);
          
          const newField: CustomField = {
            id: crypto.randomUUID(),
            label: newFieldLabel.trim(),
            value: '',
            type: 'text' as const,
            fieldKey: fieldKey
          };

          console.log(`📝 Created newField object:`, newField);

          // ✅ Add to parent component
          console.log(`📞 Calling onAddField`);
          onAddField(newField);

          // ✅ Update local state immediately for UI responsiveness
          console.log(`🔄 Updating local state`);
          setFields(prev => {
            const updated = [...prev, newField];
            console.log(`🔄 Updated fields:`, updated);
            return updated;
          });

          // ❌ Don't refresh immediately - let Wix index the new field first
          // The local state update above will show the new field in the UI
          // onFieldsChanged will be called later when needed

          console.log(`✅ Field created and added: ${newFieldLabel.trim()}`);
          
          // Clear the input but keep modal open so user can see the new field
          setNewFieldLabel('');
          
        } catch (error) {
          console.error('❌ Error creating custom field:', error);
        }
      } else {
        console.log(`⚠️ Field with this name already exists`);
      }
    } else {
      console.log(`⚠️ Label is empty or whitespace only`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (fieldToDelete) {
      try {
        console.log(`🗑️ Deleting custom field: ${fieldToDelete}`);

        await dataService.contacts.deleteExtendedField(fieldToDelete);

        // ✅ Update local state immediately for UI responsiveness
        setFields(prev => prev.filter(f => f.fieldKey !== fieldToDelete));

        // ❌ Don't refresh immediately - let Wix process the deletion first
        // The local state update above will remove the field from the UI
        // onFieldsChanged will be called later when needed

        // ✅ Keep modal open so user can see the immediate change
        // setShowModal(false); // Removed - keep modal open

        console.log(`✅ Field deleted: ${fieldToDelete}`);
        setFieldToDelete(null);
      } catch (error) {
        console.error('Error deleting custom field:', error);
      }
    }
    setShowDeleteAlert(false);
  };

  const handleRenameSave = async (field: CustomField) => {
    if (editingLabel.trim() && editingLabel !== field.label) {
      const fieldKey = field.fieldKey || `custom.${field.label.toLowerCase().replace(/\s+/g, '-')}`;
      try {
        console.log(`🔄 Renaming field: ${fieldKey} -> "${editingLabel.trim()}"`);

        await dataService.contacts.renameExtendedField(fieldKey, editingLabel.trim());

        setFields(prev => prev.map(f =>
          f.id === field.id
            ? { ...f, label: editingLabel.trim() }
            : f
        ));

        if (onFieldsChanged) {
          await onFieldsChanged();
        }

        // ✅ Keep modal open so user can see the rename result
        // setShowModal(false); // Removed - keep modal open
        console.log(`✅ Field renamed: ${fieldKey} -> "${editingLabel.trim()}"`);
      } catch (error) {
        console.error('Error renaming field:', error);
      }
    }
    setEditingField(null);
    setEditingLabel('');
  };

  const handleRenameCancel = () => {
    setEditingField(null);
    setEditingLabel('');
    console.log('✅ Rename cancelled');
  };

  return (
    <IonItem lines="none" style={hideTrigger ? { display: 'none' } : undefined}>
      {!hideTrigger && (
        <IonButton
          fill="clear"
          size="small"
          onClick={handleOpenModal}
          className={styles.addMoreButton}
        >
          <IonIcon slot="start" icon={add} className={styles.addIcon} />
          <IonText className={styles.buttonText}>{t('components.CustomFieldsManager.addMoreFields')}</IonText>
        </IonButton>
      )}
      <IonModal
        isOpen={showModal}
        onDidDismiss={handleCloseModal}
        breakpoints={[0, 1]}
        initialBreakpoint={1}
        backdropDismiss={true}
        className={styles.customFieldModal}
        mode='ios'
      >
        <IonHeader className={styles.header}>
          <IonToolbar>
            <IonItem lines="none" className={styles.addItem}>
              <IonInput
                value={newFieldLabel}
                onIonInput={e => setNewFieldLabel(e.detail.value!)}
                placeholder={t('components.CustomFieldsManager.enterCustomFieldName')}
                clearInput={true}
                autoFocus={true}
              />
              <IonButton
                slot="end"
                fill="clear"
                onClick={handleAddNew}
                disabled={!newFieldLabel.trim() || existingFields.some(f =>
                  f.label.toLowerCase() === newFieldLabel.trim().toLowerCase()
                )}
              >{t('components.CustomFieldsManager.add')}
              </IonButton>
            </IonItem>
          </IonToolbar>
        </IonHeader>

        <IonCardContent ref={sheetBodyRef} className={styles.cardContent}>
          <IonList className={styles.scrollableList}>
            {fields.map(field => (
              <IonItem
                key={field.id}
                lines="none"
                className={styles.item}
                detail={false}
              >
                {editingField === field.id ? (
                  <>
                    <IonInput
                      value={editingLabel}
                      onIonInput={e => setEditingLabel(e.detail.value!)}
                      placeholder={t('components.CustomFieldsManager.enterFieldName')}
                      autoFocus={true}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameSave(field);
                        }
                        if (e.key === 'Escape') {
                          handleRenameCancel();
                        }
                      }}
                    />
                    <IonButton
                      fill="clear"
                      slot="end"
                      onClick={() => handleRenameSave(field)}
                    >{t('components.CustomFieldsManager.save')}
                    </IonButton>
                    <IonButton
                      fill="clear"
                      slot="end"
                      color="medium"
                      onClick={handleRenameCancel}
                    >{t('components.CustomFieldsManager.cancel')}
                    </IonButton>
                  </>
                ) : (
                  <>
                    <IonLabel
                      onClick={() => {
                        if (onSelectField) {
                          onSelectField(field.id);
                          handleCloseModal();
                        }
                      }}
                    >
                      {field.label}
                    </IonLabel>

                    <IonButton
                      slot="end"
                      fill="clear"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingField(field.id);
                        setEditingLabel(field.label);
                      }}
                    >
                      <IonIcon icon={createOutline} slot="icon-only" />
                    </IonButton>

                    <IonButton
                      slot="end"
                      fill="clear"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const fieldKey = field.fieldKey || `custom.${field.label.toLowerCase().replace(/\s+/g, '-')}`;
                        setFieldToDelete(fieldKey);
                        setShowDeleteAlert(true);
                      }}
                    >
                      <IonIcon icon={trashOutline} slot="icon-only" color="danger" />
                    </IonButton>
                  </>
                )}
              </IonItem>
            ))}

            {localFields.find(f => f.label === '') && (
              <IonItem
                lines="none"
                className={`${styles.item} ${styles.blankField}`}
              >
                <IonLabel></IonLabel>
              </IonItem>
            )}
          </IonList>
        </IonCardContent>
      </IonModal>
      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => {
          setShowDeleteAlert(false);
          setFieldToDelete(null);
        }}
        header={t('components.CustomFieldsManager.confirmDelete')}
        message={t('components.CustomFieldsManager.areYouSureYouWantTo')}
        buttons={[
          {
            text: t('components.CustomFieldsManager.cancel'),
            role: 'cancel',
            cssClass: 'secondary'
          },
          {
            text: t('components.CustomFieldsManager.delete'),
            role: 'destructive',
            handler: handleDeleteConfirm
          }
        ]}
      />
    </IonItem>
  );
};